import { subtractDateTime } from '@/_utils/subtract-date-time';
import {
	BagPaymentOrganization,
	BagPaymentTermFilters,
	BagPaymentTermListParseFilters,
	BagPaymentTermParametersStatus,
	FastFilter,
} from '@/bag/bag-payment-term-list/bag-payment-term-list.model';
import { FastFiltersComponent } from '@/common/fast-filters/fast-filters.component';
import { DatePipe } from '@angular/common';
import { Component, computed, input, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Data2Service } from '@/common/data2.service';
import { DateService } from '@/common/date.service';
import {
	CancellationStatusesListReservation,
	PaymentStatuses,
	RejectedStatusesListReservation,
	Status,
} from '@/common/enums/Status';
import { FiltersComponent } from '@/common/filters.component';
import { Option } from '@/common/fast-filters/fast-filters.component';
import { PAYMENT_TERM_KIND_OPTIONS, PaymentTermKind } from '@/common/enums/PaymentTermKind';
import { PaymentTermStatus, paymentTermStatusByKindMappings } from '@/common/enums/paymentStatus';

@Component({
	selector: 'app-bag-payment-term-list-filters',
	templateUrl: './bag-payment-term-list-filters.component.html',
	styleUrls: ['./bag-payment-term-list-filters.component.scss'],
	providers: [DatePipe],
})
export class BagPaymentTermListFiltersComponent extends FiltersComponent {
	readonly agencies = input<BagPaymentOrganization[]>([]);
	readonly status = input<BagPaymentTermParametersStatus[]>([]);

	readonly fastFilterComponent = viewChild<FastFiltersComponent>('fastFilter');
	readonly selectedKind = signal<PaymentTermKind | null>(null);
	readonly statusOptions = computed<BagPaymentTermParametersStatus[]>(() => {
		const kind = this.selectedKind() ?? PaymentTermKind.PAYMENT;
		const statusesMap = paymentTermStatusByKindMappings[kind];

		return Object.entries(statusesMap).map(([id, name]) => ({
			id,
			name,
		}));
	});

	constructor(
		protected api: Data2Service,
		protected route: ActivatedRoute,
		protected router: Router,
		protected date: DateService,
		protected datePipe: DatePipe,
	) {
		super(api, route, router);
	}

	readonly parseFilters = signal<BagPaymentTermListParseFilters>({
		term__gte: this.datePipe.transform(new Date(), 'yyyy-MM-dd'),
		term__lte: this.datePipe.transform(subtractDateTime(new Date(), { days: -5 }), 'yyyy-MM-dd'),
	});

	filters: BagPaymentTermFilters = this.defaultFilters();

	readonly dateConvert: Array<keyof BagPaymentTermListParseFilters> = ['term__gte', 'term__lte'];

	readonly kindOptions = PAYMENT_TERM_KIND_OPTIONS;

	readonly fastFilters: FastFilter[] = [
		{
			id: 0,
			name: 'Rezerwacje potwierdzone +5 dni (częściowo zapłacono, nie zapłacono)',
			filters: {
				dateFrom: new Date(),
				dateTo: subtractDateTime(new Date(), { days: -5 }),
				reservationStatus: [Status.CONFIRMED],
				paymentStatuses: [PaymentStatuses.PARTIALY_PAID, PaymentStatuses.NOT_PAID],
			},
		},
		{
			id: 1,
			name: 'Wszystkie rezerwacje anulowane (częściowo zapłacono, nie zapłacono)',
			filters: {
				reservationStatus: [Status.CANCELED],
				paymentStatuses: [PaymentStatuses.PARTIALY_PAID, PaymentStatuses.NOT_PAID],
			},
		},
		{
			id: 2,
			name: 'Rezerwacje potwierdzone ze świadczeniem od dziś + 5 dni (częsciowo zapłacone, nie zapłacone)',
			filters: {
				reservationStayTimeLowerBefore: subtractDateTime(new Date(), { days: -5 }),
				reservationStayTimeLowerAfter: new Date(),
				reservationStatus: [Status.CONFIRMED],
				paymentStatuses: [PaymentStatuses.PARTIALY_PAID, PaymentStatuses.NOT_PAID],
			},
		},
	];

	protected filtersDefinition = {
		reservation_seller: {
			type: 'string',
			isArray: true,
		},
		status__in: {
			type: 'string',
			isArray: true,
		},
		kind: {
			type: 'string',
			isArray: false,
		},
	};

	updateParseFilter(key: keyof BagPaymentTermListParseFilters, value: string | null): void {
		this.parseFilters.update((current) => ({
			...current,
			[key]: value,
		}));
	}

	onKindChange(kind: string | null): void {
		this.filters.kind = kind;
		this.selectedKind.set((kind as PaymentTermKind | null) ?? null);
		this.normalizeSelectedStatuses();
	}

	selectFastFilter(selectedOption: Option | null): void {
		if (!selectedOption) {
			return;
		}
		const fastFilter = this.fastFilters.find((item) => item.id === selectedOption.id);
		if (!fastFilter?.filters) {
			return;
		}

		this.filters = this.defaultFilters();

		if (fastFilter.filters.reservationStayTimeLowerBefore) {
			this.filters.reservation_stay_time_lower_before = this.datePipe.transform(
				fastFilter.filters.reservationStayTimeLowerBefore as Date,
				'yyyy-MM-dd',
			);
		}

		if (fastFilter.filters.reservationStayTimeLowerAfter) {
			this.filters.reservation_stay_time_lower_after = this.datePipe.transform(
				fastFilter.filters.reservationStayTimeLowerAfter as Date,
				'yyyy-MM-dd',
			);
		}

		if (fastFilter.filters.dateFrom) {
			this.filters.term__gte = this.datePipe.transform(fastFilter.filters.dateFrom as Date, 'yyyy-MM-dd');
			this.updateParseFilter('term__gte', this.filters.term__gte);
		}

		if (fastFilter.filters.dateTo) {
			this.filters.term__lte = this.datePipe.transform(fastFilter.filters.dateTo as Date, 'yyyy-MM-dd');
			this.updateParseFilter('term__lte', this.filters.term__lte);
		}

		if (fastFilter.filters.paymentStatuses) {
			this.filters.status__in = fastFilter.filters.paymentStatuses as string[];
		}

		if (fastFilter.filters.reservationStatus) {
			this.applyReservationStatusFilter(fastFilter.filters.reservationStatus as Status[]);
		}

		this.selectedKind.set((this.filters.kind as PaymentTermKind | null) ?? null);
		this.normalizeSelectedStatuses();
		this.filters2url(this.filters);
	}

	filtersSave(): void {
		this.convertSaveFilters();
		this.filters2url(this.filters);
		this.api.put('/userFilters/' + this.userFilters, this.filters, () => {});
	}

	filtersClear(): void {
		for (const key in this.filters) {
			if (Array.isArray(this.filters[key])) {
				this.filters[key] = [];
				continue;
			}
			this.filters[key] = null;
		}

		this.parseFilters.set({
			term__gte: null,
			term__lte: null,
		});

		const fastFilter = this.fastFilterComponent();
		if (!fastFilter) {
			return;
		}
		fastFilter.clear();
	}

	protected convertSaveFilters(): void {
		this.filters.table_name__in = 'reservation_reservation';
		const parseFilters = this.parseFilters();
		Object.keys(parseFilters).forEach((key) => {
			const parseFilterKey = key as keyof BagPaymentTermListParseFilters;
			if (!parseFilters[parseFilterKey]) {
				this.filters[key] = null;
				return;
			}

			if (this.dateConvert.includes(parseFilterKey)) {
				this.filters[key] = this.datePipe.transform(parseFilters[parseFilterKey], 'yyyy-MM-dd');
			}
		});
	}

	protected filtersAfterLoad(): void {
		this.parseFilters.set({
			term__gte: this.filters.term__gte,
			term__lte: this.filters.term__lte,
		});
		this.selectedKind.set((this.filters.kind as PaymentTermKind | null) ?? null);
		this.normalizeSelectedStatuses();
	}

	protected filters2url(filters: BagPaymentTermFilters): void {
		if (!filters || filters instanceof Array) {
			this.selected.emit({});
			return;
		}
		filters.on = 1;
		this.router.navigate(['.'], {
			relativeTo: this.route,
			queryParams: filters,
			preserveFragment: true,
		});
	}

	private applyReservationStatusFilter(reservationStatuses: Status[]): void {
		this.filters.reservation_status = [];
		reservationStatuses.forEach((status) => {
			switch (status) {
				case Status.CANCELED:
					this.filters.reservation_status.push(...CancellationStatusesListReservation);
					break;
				case Status.REJECTED:
					this.filters.reservation_status.push(...RejectedStatusesListReservation);
					break;
				case Status.OPTION:
					this.filters.reservation_status.push(Status.OPTION);
					break;
				case Status.CONFIRMED:
					this.filters.reservation_status.push(Status.CONFIRMED);
					break;
				default:
					this.filters.reservation_status = [];
					break;
			}
		});
	}

	private normalizeSelectedStatuses(): void {
		const availableStatuses = new Set(this.statusOptions().map((status) => status.id));
		this.filters.status__in = this.filters.status__in.filter((status) =>
			availableStatuses.has(status as PaymentTermStatus),
		);
	}

	private defaultFilters(): BagPaymentTermFilters {
		return {
			reservation_status: [],
			reservation_seller: [],
			reservation_organization: [],
			count_kind: null,
			kind: null,
			status__in: [],
			currency__in: [],
			table_id__in: null,
			table_name__in: null,
			term__gte: null,
			term__lte: null,
			value__gte: null,
			value__lt: null,
			seller_is_oskar: '',
			offer_product: [],
			reservation_stay_time_lower_after: null,
			reservation_stay_time_lower_before: null,
			reservation_stay_time_upper_after: null,
			reservation_stay_time_upper_before: null,
			source: null,
		};
	}
}
